import { useState, useEffect } from "react";

/**
 * Hook pour gérer la validation et l'initialisation des dates
 * - Calcul du mois précédent par défaut
 * - Validation des mois futurs
 * - Gestion du changement d'année avec impact sur les mois
 *
 * @param {Object} options - Options de configuration
 * @param {string} options.initialMonth - Mois initial (optionnel)
 * @param {string} options.initialYear - Année initiale (optionnel)
 * @returns {Object} État et fonctions de gestion des dates
 */
export const useDateValidation = ({
  initialMonth = null,
  initialYear = null,
} = {}) => {
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);

  /**
   * Calcule le mois précédent par rapport à la date actuelle
   * @returns {Object} { month: string, year: string }
   */
  const getPreviousMonth = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    let previousMonth = currentDate.getMonth(); // 0-11
    let yearForPreviousMonth = currentYear;

    // Si nous sommes en janvier (mois 0), le mois précédent est décembre (12) de l'année précédente
    if (previousMonth === 0) {
      previousMonth = 12; // Décembre
      yearForPreviousMonth = currentYear - 1;
    }

    return {
      month: previousMonth.toString(),
      year: yearForPreviousMonth.toString(),
    };
  };

  /**
   * Vérifie si un mois est dans le futur
   * @param {string} month - Mois à vérifier (1-12)
   * @param {string} year - Année à vérifier
   * @returns {boolean}
   */
  const isMonthInFuture = (month, year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    if (year > currentYear) {
      return true;
    }

    if (year === currentYear && parseInt(month) > currentMonth) {
      return true;
    }

    return false;
  };

  /**
   * Vérifie si un mois est disponible pour sélection
   * @param {string} month - Mois à vérifier (1-12)
   * @param {string} year - Année à vérifier
   * @returns {boolean}
   */
  const isMonthAvailable = (month, year) => {
    return !isMonthInFuture(month, year);
  };

  /**
   * Obtient la liste des mois disponibles pour une année donnée
   * @param {string} year - Année pour laquelle obtenir les mois disponibles
   * @returns {number[]} Liste des mois disponibles (1-12)
   */
  const getAvailableMonths = (year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    // Si l'année est dans le passé, tous les mois sont disponibles
    if (year < currentYear) {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }

    // Si l'année est l'année courante, seuls les mois jusqu'au mois courant sont disponibles
    if (year === currentYear) {
      return Array.from({ length: currentMonth }, (_, i) => i + 1);
    }

    // Si l'année est dans le futur, aucun mois n'est disponible
    return [];
  };

  /**
   * Handler pour le changement d'année avec ajustement automatique du mois si nécessaire
   * @param {string} newYear - Nouvelle année sélectionnée
   */
  const handleYearChange = (newYear) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = currentDate.getMonth() + 1;

    setSelectedYear(newYear);

    // Si l'année sélectionnée est l'année courante et le mois sélectionné est dans le futur
    if (
      newYear === currentYear &&
      selectedMonth &&
      parseInt(selectedMonth) > currentMonth
    ) {
      // Revenir au mois courant
      setSelectedMonth(currentMonth.toString());
    }
  };

  /**
   * Handler pour le changement de mois
   * @param {string} newMonth - Nouveau mois sélectionné
   */
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
  };

  /**
   * Réinitialise les dates au mois précédent
   */
  const resetToDefaultDate = () => {
    const { month, year } = getPreviousMonth();
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  // Initialisation automatique au mois précédent si pas de valeur initiale
  useEffect(() => {
    if (!selectedMonth || !selectedYear) {
      const { month, year } = getPreviousMonth();
      if (!selectedMonth) setSelectedMonth(month);
      if (!selectedYear) setSelectedYear(year);
    }
  }, []);

  return {
    // State
    selectedMonth,
    selectedYear,

    // Setters
    setSelectedMonth,
    setSelectedYear,

    // Handlers
    handleMonthChange,
    handleYearChange,

    // Utilities
    getPreviousMonth,
    isMonthInFuture,
    isMonthAvailable,
    getAvailableMonths,
    resetToDefaultDate,
  };
};
